/**
 * src/pages/ContactPage.js — Simple contact info page (no form, no WhatsApp CTA)
 */
import React from 'react';
import { Link } from 'react-router-dom';

const CONTACT_DETAILS = [
  { icon: '📍', label: 'Address',  value: '4/214, Near Enammeenatchipuram Bus Stop, Vembakottai, Sivakasi, Tamil Nadu — 626 131' },
  { icon: '📞', label: 'Phone',    value: '+91 63748 01083', href: 'tel:+916374801083' },
  { icon: '✉️', label: 'Email',    value: 'lakshmicrackersonline@gmail.com', href: 'mailto:lakshmicrackersonline@gmail.com' },
  { icon: '⏰', label: 'Hours',    value: 'Mon – Sat: 9 AM – 8 PM IST' },
];

const FAQ = [
  {
    q: 'How do I place an order?',
    a: 'Browse our products, add items to your cart, and submit your enquiry. Our team will call you within 24 hours to confirm and complete your order.',
  },
  {
    q: 'Is online sale of firecrackers legal?',
    a: 'We follow all Supreme Court of India guidelines. We operate as an enquiry platform — actual order confirmation and payment happen through our personal phone follow-up.',
  },
  {
    q: 'What areas do you deliver to?',
    a: 'We currently deliver across Tamil Nadu and major cities in South India. Shipping charges and delivery timelines are confirmed during your order follow-up call.',
  },
  {
    q: 'Can I cancel or change my order?',
    a: 'Yes — as long as your order has not been dispatched. Please call us immediately and we will do our best to accommodate your request.',
  },
  {
    q: 'What is the minimum order value?',
    a: 'The minimum order value is ₹3,000. 100% advance payment is required along with your order confirmation.',
  },
];

export default function ContactPage() {
  // ── SEO: update page title and meta description ───────────────
  React.useEffect(() => {
    document.title = 'Contact Us — Lakshmi Crackers | Call +91 63748 01083';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Contact Lakshmi Crackers for bulk orders, corporate gifting, or any enquiries. Call or WhatsApp +91 63748 01083. Located in Sivakasi, Tamil Nadu.');
    return () => {
      document.title = 'Lakshmi Crackers 🪔 — Premium Fireworks';
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#fffaf0' }}>

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-red-950 via-red-900 to-red-800 overflow-hidden">
        <div className="absolute inset-0 deco-stripes opacity-30 pointer-events-none" />
        <div className="absolute top-0 right-0 w-80 h-80 bg-yellow-400/10 rounded-full blur-[80px] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <span className="inline-flex items-center gap-2 bg-yellow-400/15 border border-yellow-400/25
            text-yellow-300 text-xs font-bold px-5 py-2 rounded-full uppercase tracking-widest mb-5">
            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse" />
            We respond within 24 hours
          </span>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-3"
            style={{ fontFamily: '"Playfair Display",serif' }}>
            Contact Us
          </h1>
          <p className="text-red-100/70 text-base max-w-xl mx-auto leading-relaxed">
            Have a question about an order, product, or delivery?
            Reach out to us directly — we're happy to help!
          </p>
        </div>
        <svg className="absolute bottom-0 left-0 right-0 w-full h-8" viewBox="0 0 1440 32" fill="none" preserveAspectRatio="none">
          <path d="M0,20 C480,40 960,0 1440,20 L1440,32 L0,32 Z" fill="#fffaf0" />
        </svg>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-2 gap-8">

          {/* Contact details card */}
          <div className="bg-white rounded-3xl border border-gray-100
            shadow-[0_4px_20px_rgba(0,0,0,0.06)] p-8">
            <h2 className="font-black text-gray-900 text-xl mb-2"
              style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
              Get in Touch
            </h2>
            <p className="text-gray-500 text-sm mb-7 leading-relaxed">
              For any queries, orders, or assistance — please reach out to us
              directly on the contact details below.
            </p>
            <ul className="space-y-5">
              {CONTACT_DETAILS.map(({ icon, label, value, href }) => (
                <li key={label} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-red-50 border border-red-100
                    flex items-center justify-center text-xl flex-shrink-0">
                    {icon}
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">
                      {label}
                    </p>
                    {href ? (
                      <a href={href}
                        className="text-sm font-bold text-red-700 hover:text-red-900 transition-colors">
                        {value}
                      </a>
                    ) : (
                      <p className="text-sm font-semibold text-gray-700">{value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* CTA to products */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-gray-500 text-sm mb-4">
                Ready to order? Browse our collection and place an enquiry.
              </p>
              <Link to="/products"
                className="inline-flex items-center gap-2
                  bg-gradient-to-br from-red-600 to-red-800 text-white font-black
                  px-6 py-3 rounded-2xl shadow-lg shadow-red-600/25
                  hover:shadow-red-600/40 hover:-translate-y-0.5 transition-all duration-200 text-sm">
                🎆 Browse Products
              </Link>
            </div>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-3xl border border-gray-100
            shadow-[0_4px_20px_rgba(0,0,0,0.06)] p-8">
            <h2 className="font-black text-gray-900 text-xl mb-6"
              style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
              Frequently Asked
            </h2>
            <div className="space-y-5">
              {FAQ.map((f, i) => (
                <div key={i} className="border-b border-gray-100 pb-5 last:border-0 last:pb-0">
                  <p className="font-bold text-gray-900 text-sm mb-1.5">{f.q}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
